import { useCallback, useEffect, useMemo, useState } from "react";
import { isExceptionVar, maybeBuildGroupedFilters } from "../utils/chartHelpers";

export const useChartFilters = ({ selectedIndependent, selectedDependent, questionsIndex }) => {
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedDependentFilters, setSelectedDependentFilters] = useState([]);
  const [isDepModalOpen, setIsDepModalOpen] = useState(false);

  const selectedIndependentMeta = selectedIndependent ? questionsIndex[selectedIndependent] : null;
  const groupedIndependent = useMemo(
    () =>
      maybeBuildGroupedFilters({
        mainQuestion: selectedIndependentMeta?.mainQuestion,
        answersMapping: selectedIndependentMeta?.answersMapping,
      }),
    [selectedIndependentMeta]
  );

  const indepIsGrouped = Boolean(groupedIndependent?.grouped);
  const indepGroupMembers = useMemo(
    () => groupedIndependent?.groupMembers || {},
    [groupedIndependent]
  );
  const indepGroupDisplay = useMemo(
    () => groupedIndependent?.displayByGroupId || {},
    [groupedIndependent]
  );

  const filters = useMemo(() => {
    if (!selectedIndependentMeta) return [];
    if (indepIsGrouped) return groupedIndependent?.filters || [];

    return Object.entries(selectedIndependentMeta.answersMapping || {}).map(([id, value]) => ({
      id,
      display: value.Display,
    }));
  }, [selectedIndependentMeta, indepIsGrouped, groupedIndependent]);

  const dependentAnswerMapping = useMemo(() => {
    const selectedDependentMeta = selectedDependent ? questionsIndex[selectedDependent] : null;
    return selectedDependentMeta?.answersMapping || {};
  }, [selectedDependent, questionsIndex]);

  const dependentFilters = useMemo(
    () =>
      Object.entries(dependentAnswerMapping).map(([id, value]) => ({
        id,
        display: value.Display,
      })),
    [dependentAnswerMapping]
  );

  useEffect(() => {
    setSelectedFilters([]);
    setIsModalOpen(false);
  }, [selectedIndependent]);

  useEffect(() => {
    setSelectedDependentFilters([]);
    setIsDepModalOpen(false);
  }, [selectedDependent]);

  const handleFilterChange = useCallback((event) => {
    const selectedValues = Array.from(event.target.options)
      .filter((option) => option.selected)
      .map((option) => option.value);
    setSelectedFilters(selectedValues);
  }, []);

  const handleDependentFilterChange = useCallback((event) => {
    const selectedValues = Array.from(event.target.options)
      .filter((option) => option.selected)
      .map((option) => option.value);
    setSelectedDependentFilters(selectedValues);
  }, []);

  const toggleModal = useCallback(() => {
    setIsModalOpen((open) => !open);
  }, []);

  const toggleDepModal = useCallback(() => {
    setIsDepModalOpen((open) => !open);
  }, []);

  const applyActiveFilters = useCallback(
    (rows = []) => {
      let filteredRows = [...rows];
      const hasIndependentSelection = Boolean(selectedIndependent);

      if (hasIndependentSelection && selectedFilters.length > 0) {
        if (indepIsGrouped && Object.keys(indepGroupMembers).length) {
          const memberSets = selectedFilters.map(
            (groupId) => new Set((indepGroupMembers[groupId] || []).map(String))
          );
          filteredRows = filteredRows.filter((row) => {
            const raw = String(row[selectedIndependent]);
            return memberSets.some((set) => set.has(raw));
          });
        } else {
          const rawSet = new Set(selectedFilters.map(String));
          filteredRows = filteredRows.filter((row) => rawSet.has(String(row[selectedIndependent])));
        }
      }

      if (selectedDependent && selectedDependentFilters.length > 0) {
        const dependentSet = new Set(selectedDependentFilters.map(String));
        filteredRows = filteredRows.filter((row) =>
          dependentSet.has(String(row[selectedDependent]))
        );
      }

      const filterIndependentNa = hasIndependentSelection && !isExceptionVar(selectedIndependent);
      const filterDependentNa = Boolean(selectedDependent) && !isExceptionVar(selectedDependent);
      if (filterIndependentNa || filterDependentNa) {
        filteredRows = filteredRows.filter((row) => {
          const independentValue = hasIndependentSelection ? row[selectedIndependent] : null;
          const dependentValue = selectedDependent ? row[selectedDependent] : null;
          const independentInvalid =
            filterIndependentNa &&
            (String(independentValue) === "-99" || Number(independentValue) === -99);
          const dependentInvalid =
            filterDependentNa &&
            (String(dependentValue) === "-99" || Number(dependentValue) === -99);
          return !(independentInvalid || dependentInvalid);
        });
      }

      return filteredRows;
    },
    [
      selectedFilters,
      indepIsGrouped,
      indepGroupMembers,
      selectedIndependent,
      selectedDependentFilters,
      selectedDependent,
    ]
  );

  return {
    filters,
    selectedFilters,
    isModalOpen,
    toggleModal,
    handleFilterChange,

    dependentAnswerMapping,
    dependentFilters,
    selectedDependentFilters,
    isDepModalOpen,
    toggleDepModal,
    handleDependentFilterChange,

    indepIsGrouped,
    indepGroupMembers,
    indepGroupDisplay,
    applyActiveFilters,
  };
};
